import { useEffect, useMemo, useState } from 'react';

import { Outlet, useLocation, useParams } from 'react-router-dom';

import { getEnvVariable } from '@appcharge/shared-ui';
import usePublisher from 'api/usePublisher';
import { useUserDetails } from 'api/useUserDetails';
import useFeatureFlags from 'api/useFeatureFlags';
import {
  EPublisherSolutionModel,
  EPublisherSolutionModelSubType,
  Project
} from 'common/contracts';
import { EFeatureFlag, ELocalStorageKeys } from 'constants/enums';
import AcSidebar from 'design-system/AcSidebar';
import throttle from 'lodash/throttle';
import { setupChecklistStateService } from 'state/setup-checklist.state.service';
import { localStorageUtil } from 'utils/localStorageUtil';
import { v4 as uuidv4 } from 'uuid';

import { SendAgreementModal } from 'components/SendContractModal/Index';
import { SidebarProvider } from 'components/ui/sidebar';
import { VerifyBusinessModal } from 'components/VerifyBusinessModal/Index';

import { Header } from './Header';
import { SetupChecklistContainer } from './SetupChecklist/Container';
import { WipBanner } from './WipBanner';

const TEST_INACTIVITY_LIMIT = 60 * 60 * 1000;
const CHECK_INTERVAL = 60 * 1000;
const THROTTLE_INTERVAL = 2000;

const THEME_STORE_SETTINGS_PATH = '/theme/store-settings';

export const Layout: React.FC = () => {
  const location = useLocation();
  const { publisherId: projectPublisherId } = useParams();
  const isThemeStoreSettingsPage = location.pathname.includes(
    THEME_STORE_SETTINGS_PATH
  );
  const { userDetails } = useUserDetails();
  const { featureFlags } = useFeatureFlags({
    publisherId: projectPublisherId
  });
  const { getPublisher } = usePublisher(projectPublisherId);
  const [showBanner, setShowBanner] = useState(() => {
    const savedState = localStorageUtil.get(
      ELocalStorageKeys.WIP_BANNER_CLOSED
    );
    return savedState === null;
  });

  const publisherId = useMemo(
    () =>
      userDetails?.projects?.find(
        ({ projectType }: Project) => projectType === EPublisherSolutionModel.CHECKOUT
      )?.publisherId,
    [userDetails?.projects]
  );
  const userEvents = ['mousemove', 'keydown', 'scroll'];

  const setupSessionInactivityHandler = () => {
    let lastActivityTime = Date.now();

    const activityHandler = throttle(() => {
      lastActivityTime = Date.now();
    }, THROTTLE_INTERVAL);

    userEvents.forEach((event) =>
      window.addEventListener(event, activityHandler)
    );

    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastActivityTime > TEST_INACTIVITY_LIMIT) {
        const newSessionId = uuidv4();
        localStorageUtil.set(ELocalStorageKeys.SESSION_ID, newSessionId);
        lastActivityTime = now;
      }
    }, CHECK_INTERVAL);

    return () => {
      userEvents.forEach((event) =>
        window.removeEventListener(event, activityHandler)
      );
      clearInterval(interval);
    };
  };

  useEffect(() => {
    const sessionId = localStorageUtil.get(ELocalStorageKeys.SESSION_ID);
    if (!sessionId) {
      const newSessionId = uuidv4();
      localStorageUtil.set(ELocalStorageKeys.SESSION_ID, newSessionId);
    }
    const cleanup = setupSessionInactivityHandler();
    return cleanup;
  }, []);

  const handleCloseBanner = () => {
    setShowBanner(false);
    localStorageUtil.set(ELocalStorageKeys.WIP_BANNER_CLOSED, 'true');
  };

  const shouldSeeOnBoardingWidget: boolean = useMemo(
    () => userDetails?.isOnboarding || false,
    [userDetails]
  );

  const shouldProjectOnboardingWidgetBeShown = useMemo(() => {
    const currentProject = userDetails?.projects?.find(
      (project: Project) => project.publisherId === projectPublisherId
    );

    if (
      !currentProject?.isOnboarding ||
      currentProject?.projectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS
    ) {
      return false;
    }

    const isWebCheckout =
      currentProject?.projectType === EPublisherSolutionModel.CHECKOUT &&
      currentProject?.projectSubType === EPublisherSolutionModelSubType.WEB_CHECKOUT;

    const featureFlag = isWebCheckout
      ? EFeatureFlag.DASHBOARD_CHECKOUT_CHECKLIST_WIDGET
      : EFeatureFlag.DASHBOARD_CHECKLIST_WIDGET;

    return (
      featureFlags?.[featureFlag] &&
      !!getPublisher.data?.showOnboardWidget
    );
  }, [
    projectPublisherId,
    userDetails?.projects,
    featureFlags,
    getPublisher.data?.showOnboardWidget
  ]);

  const shouldEnvIndicatorBeShown =
    getEnvVariable('REACT_APP_ENV') === 'sandbox';

  useEffect(() => {
    const shouldShowWidget = shouldSeeOnBoardingWidget || shouldProjectOnboardingWidgetBeShown;
    setupChecklistStateService.setShouldWidgetBeShown(shouldShowWidget);
  }, [shouldSeeOnBoardingWidget, shouldProjectOnboardingWidgetBeShown]);

  return (
    <>
      {shouldEnvIndicatorBeShown && (
        <div className="fixed top-0 left-0 right-0 z-1000 flex w-full py-2 px-6 bg-indigo-400 text-white text-xs pl-[26px]">
          <span className="font-bold mr-[9px]">Sandbox: </span> You're using
          sandbox mode
        </div>
      )}
      <SidebarProvider>
        <AcSidebar />
        <div
          className={[
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
            shouldEnvIndicatorBeShown ? 'pt-8' : '',
            isThemeStoreSettingsPage ? 'h-svh max-h-svh min-h-0' : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Header />
          <main
            className={`flex min-h-0 min-w-0 flex-1 flex-col ${
              isThemeStoreSettingsPage ? 'overflow-hidden' : 'overflow-auto'
            }`}
          >
            <Outlet />
          </main>
          {showBanner && !publisherId && (
            <WipBanner onCloseIconClick={handleCloseBanner} />
          )}
          {shouldSeeOnBoardingWidget && (
            <>
              <SetupChecklistContainer />
              <VerifyBusinessModal />
              <SendAgreementModal />
            </>
          )}
          {shouldProjectOnboardingWidgetBeShown && <SetupChecklistContainer />}
        </div>
      </SidebarProvider>
    </>
  );
};
